import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'

import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import MarkdownDisplay from '@/components/MarkdownDisplay/MarkdownDisplay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/utils/common'

import {
  CONTENT_PREVIEW_LENGTH,
  SEARCH_DEBOUNCE_DELAY,
  TEMPLATE_DEFAULT_SORT,
  TEMPLATE_LIST_PAGINATION_LIMIT,
} from './constants'
import { listTemplates } from './service'
import type { CommentTemplate } from './types'

export interface CommentTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectTemplate: (template: CommentTemplate) => void
}

export const CommentTemplateDialog = ({
  open,
  onOpenChange,
  onSelectTemplate,
}: Readonly<CommentTemplateDialogProps>) => {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<CommentTemplate[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const fetchTemplates = useCallback(async (query: string) => {
    setIsLoading(true)
    try {
      const response = await listTemplates({
        q: query,
        sort_by: TEMPLATE_DEFAULT_SORT,
        limit: TEMPLATE_LIST_PAGINATION_LIMIT,
      })
      setTemplates(response.data ?? [])
    } catch {
      setTemplates([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const timeoutId = setTimeout(() => {
      fetchTemplates(searchQuery)
    }, SEARCH_DEBOUNCE_DELAY)

    return () => clearTimeout(timeoutId)
  }, [open, searchQuery, fetchTemplates])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      setSelectedId(null)
      setExpandedIds(new Set())
    }
  }, [open])

  const handleBack = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleTemplateClick = useCallback((templateId: string) => {
    setSelectedId(templateId)
  }, [])

  const handleAddTemplate = useCallback(() => {
    const selected = templates.find((t) => t.id === selectedId)
    if (selected) {
      onSelectTemplate(selected)
      onOpenChange(false)
    }
  }, [templates, selectedId, onSelectTemplate, onOpenChange])

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent, templateId: string) => {
      e.stopPropagation()
      setExpandedIds((prev) => {
        const next = new Set(prev)
        if (next.has(templateId)) {
          next.delete(templateId)
        } else {
          next.add(templateId)
        }
        return next
      })
    },
    []
  )

  const renderContent = (template: CommentTemplate) => {
    const isExpanded = expandedIds.has(template.id)
    const needsTruncation = template.content.length > CONTENT_PREVIEW_LENGTH

    if (!needsTruncation) {
      return (
        <MarkdownDisplay
          content={template.content}
          className="text-sm text-gray-600"
        />
      )
    }

    if (isExpanded) {
      return (
        <>
          <MarkdownDisplay
            content={template.content}
            className="text-sm text-gray-600"
          />
          <button
            type="button"
            className="text-sm text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer p-0"
            onClick={(e) => handleToggleExpand(e, template.id)}
          >
            {t('common.seeLess')}
          </button>
        </>
      )
    }

    return (
      <>
        <MarkdownDisplay
          content={template.content}
          className="text-sm text-gray-600 line-clamp-2"
        />
        <button
          type="button"
          className="text-sm text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer p-0"
          onClick={(e) => handleToggleExpand(e, template.id)}
        >
          {t('common.seeMore')}
        </button>
      </>
    )
  }

  // Render loading state
  const renderLoading = () => (
    <div className="flex items-center justify-center h-32">
      <Spinner className="h-6 w-6" />
    </div>
  )

  // Render empty state
  const renderEmpty = () => (
    <EmptySearchResult
      translationKey="commentTemplate"
      showImage={false}
      className="flex flex-col items-center justify-center h-32 text-gray-500 text-sm"
    />
  )

  // Render template list
  const renderTemplateList = () => (
    <>
      {templates.map((template) => (
        <button
          key={template.id}
          type="button"
          onClick={() => handleTemplateClick(template.id)}
          className={cn(
            'w-full text-left px-4 py-3 rounded-lg border transition-all cursor-pointer bg-white group hover:shadow-md',
            selectedId === template.id
              ? 'border-blue-500 ring-1 ring-blue-500'
              : 'border-solid border-gray-200 hover:border-gray-300'
          )}
        >
          <span className="font-semibold text-gray-900 block">
            {template.name}
          </span>
          {renderContent(template)}
        </button>
      ))}
    </>
  )

  // Main content render
  const renderMainContent = () => {
    if (isLoading) {
      return renderLoading()
    }

    if (templates.length === 0) {
      // Use the same empty state component for both cases as they currently share the same configuration
      return renderEmpty()
    }

    return renderTemplateList()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[720px] max-h-[600px] p-0 gap-0"
        hideCloseButton
      >
        {/* Header */}
        <DialogHeader className="px-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleBack}
              className="p-1 hover:bg-gray-100 rounded-md cursor-pointer bg-transparent border-none text-foreground-fg-quaternary-400"
              aria-label={t('common.back')}
            >
              <ArrowLeft size={20} />
            </Button>
            <DialogTitle className="text-lg font-semibold p-0">
              {t('commentTemplate.title')}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[60vh]">
          {/* Search */}
          <div className="bg-white px-4 py-2">
            <InputSearch
              searchText={searchQuery}
              setSearchText={setSearchQuery}
              placeholder={t('commentTemplate.searchPlaceholder')}
              className="w-full max-w-full"
            />
          </div>

          {/* Template List */}
          <div className="px-4 pb-4 space-y-2">{renderMainContent()}</div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <Button
            variant="solid"
            color="primary"
            className="rounded-xl"
            onClick={handleAddTemplate}
            disabled={!selectedId}
          >
            {t('commentTemplate.addTemplate')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
